import React from 'react';
import Fade from 'react-reveal/Fade';

import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FormControlComponent } from 'design/formControl';
import { PressControlComponent } from 'design/pressControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { PhoneFieldComponent } from 'design/phoneField';
import { useServices } from 'hooks/useServices';
import { ParagraphComponent } from 'design/paragraph';
import { MarginComponent } from 'design/margin';
import { InvalidPhoneVerificationCodeError } from 'utils/errors';
import { usePhoneConfirmationForm } from 'hooks/usePhoneConfirmationForm';
import {
  createPhoneConfirmationFormValues,
  validatePhoneConfirmationFormValues,
  validatePhoneConfirmationCodeFormValues,
} from 'utils/validatePhoneConfirmationFormValues';
import { normalizePhoneNumber } from 'utils/normalizePhoneNumber';
import { SmallPressControlComponent } from 'design/smallPressControl';
import { CodeInputLayoutComponent } from 'design/codeInputLayout';
import { CharInputs } from 'containers/CharInputs';
import { SpinnerComponent } from 'design/spinner';
import { GridContainerComponent } from 'design/grid';
import { ContentLayoutComponent } from 'design/contentLayout';
import { ANIMATION_DEFAULT_DURATION } from 'utils/constants';
import { useIsMobile } from 'hooks/useIsMobile';
import { useIntlCode } from 'hooks/useIntlCode';
import { useAppDispatcher } from 'hooks/useAppDispatcher';

type Props = {
  onSubmitSucceed(): void;
};

type ProcessName = 'PHONE_FORM_SUBMISSION' | 'CODE_FORM_SUBMISSION';

export const ProdStackManagerPhoneConfirm = (props: Props) => {
  const { onSubmitSucceed } = props;

  const [processName, setProcessName] = React.useState<null | ProcessName>(
    null
  );

  const isMobile = useIsMobile();

  const langCode = useIntlCode();

  const {
    sendAuthorisationCodeOnPhone,
    checkAuthorisationPhoneCode,
  } = useServices();

  const dispatch = useAppDispatcher();

  const intl = useIntlDictionary();

  const {
    prodStackManager: { phoneConfirm: ownDictionary },
  } = intl;

  const {
    values,
    violations,
    setValues,
    setViolations,
    setPhoneValue,
    setCodeValue,
  } = usePhoneConfirmationForm();

  const { injectPromise } = useAsyncBoundary();

  const [isCodeSent, setIsCodeSent] = React.useState(false);

  const submitPhoneForm = React.useCallback(() => {
    setViolations([]);

    const nextViolations = validatePhoneConfirmationFormValues(values);

    if (nextViolations.length > 0) {
      Promise.resolve(nextViolations).then(setViolations);
    } else {
      setProcessName('PHONE_FORM_SUBMISSION');

      const promise = sendAuthorisationCodeOnPhone({
        phone: normalizePhoneNumber(values.phone),
      })
        .then(() => {
          setIsCodeSent(true);
          setProcessName(null);
        })
        .catch(() => {
          setViolations([{ name: 'SendCodeFailed' }]);
          setProcessName(null);
        });

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [injectPromise, sendAuthorisationCodeOnPhone, setViolations, values]);

  const submitConfirmationWithCodeForm = React.useCallback(() => {
    const nextViolations = validatePhoneConfirmationCodeFormValues(values);

    if (nextViolations.length === 0) {
      setProcessName('CODE_FORM_SUBMISSION');

      const promise = checkAuthorisationPhoneCode({
        phone: normalizePhoneNumber(values.phone),
        code: values.code.join(''),
      })
        .then(() => {
          dispatch({
            name: 'ProdStackManager__PhoneConfirmed',
            phone: normalizePhoneNumber(values.phone),
          });
          onSubmitSucceed();
        })
        .catch((error) => {
          if (error instanceof InvalidPhoneVerificationCodeError) {
            setValues((prev) => ({
              ...prev,
              code: createPhoneConfirmationFormValues().code,
            }));
            setViolations([{ name: 'InvalidCode' }]);
            setProcessName(null);
          } else {
            throw error;
          }
        });

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [
    checkAuthorisationPhoneCode,
    dispatch,
    injectPromise,
    onSubmitSucceed,
    setValues,
    setViolations,
    values,
  ]);

  React.useEffect(() => {
    if (isCodeSent) {
      submitConfirmationWithCodeForm();
    }
  }, [isCodeSent, submitConfirmationWithCodeForm]);

  const submit = React.useCallback(() => {
    if (isCodeSent) {
      submitConfirmationWithCodeForm();
    } else {
      submitPhoneForm();
    }
  }, [isCodeSent, submitConfirmationWithCodeForm, submitPhoneForm]);

  const changePhone = React.useCallback(() => {
    setIsCodeSent(false);
    setCodeValue(createPhoneConfirmationFormValues().code);
    setViolations([]);
  }, [setCodeValue, setViolations]);

  const phoneHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'PhoneInvalid':
          return ownDictionary.phoneInvalidText;
        case 'SendCodeFailed':
          return ownDictionary.sendCodeFallbackText;
      }
    }
  }, [ownDictionary, violations]);

  const phoneIsInvalid = React.useMemo(
    () =>
      violations.some(
        (violation) =>
          violation.name === 'PhoneInvalid' ||
          violation.name === 'SendCodeFailed'
      ),
    [violations]
  );

  const codeIsInvalid = violations.some(
    (violation) => violation.name === 'InvalidCode'
  );

  const invalidDigitIndexes = React.useMemo(() => {
    if (violations.some((violation) => violation.name === 'InvalidCode')) {
      return [0, 1, 2, 3, 4, 5];
    }
  }, [violations]);

  const hasViolatedField = violations.length > 0;

  return (
    <FormControlComponent
      shouldFocusFirstInvalidInput={hasViolatedField}
      onSubmit={submit}
    >
      <ContentLayoutComponent
        title={
          <TextComponent size="l" family="futura">
            {ownDictionary.titleText}
          </TextComponent>
        }
        body={
          <>
            <FloatingLabelComponent
              lifted
              invalid={phoneIsInvalid}
              titleText={
                <TextComponent color="secondary" size="m">
                  {ownDictionary.phoneInputLabelText}
                </TextComponent>
              }
              hintText={
                <TextComponent color="critical" size="s">
                  {phoneHint}
                </TextComponent>
              }
            >
              <GridContainerComponent>
                <PhoneFieldComponent
                  autoFocus
                  value={values.phone}
                  onChange={setPhoneValue}
                  disabled={processName === 'PHONE_FORM_SUBMISSION'}
                  readOnly={isCodeSent}
                  invalid={phoneIsInvalid}
                  locale={langCode}
                  countrySearchPlaceholderText={
                    ownDictionary.phoneCountrySearchPlaceholder
                  }
                  countryNotFoundText={ownDictionary.phoneCountryNotFound}
                />
                {isCodeSent && (
                  <SmallPressControlComponent
                    disabled={processName === 'CODE_FORM_SUBMISSION'}
                    onPress={changePhone}
                  >
                    {ownDictionary.changePhoneActionText}
                  </SmallPressControlComponent>
                )}
              </GridContainerComponent>
            </FloatingLabelComponent>

            <Fade
              duration={ANIMATION_DEFAULT_DURATION}
              delay={isCodeSent ? ANIMATION_DEFAULT_DURATION : 0}
              when={isCodeSent}
              mountOnEnter
              unmountOnExit
              collapse
              bottom
            >
              <MarginComponent top={30}>
                <ParagraphComponent>
                  {codeIsInvalid ? (
                    <TextComponent color="critical">
                      {ownDictionary.codeInvalidLabel}
                    </TextComponent>
                  ) : (
                    <TextComponent>{ownDictionary.codeLabel}</TextComponent>
                  )}
                </ParagraphComponent>
                <CodeInputLayoutComponent
                  theme="emailCode"
                  inputs={
                    <CharInputs
                      name="phoneCode"
                      chars={values.code}
                      onCharsChange={setCodeValue}
                      invalidCharIndexes={invalidDigitIndexes}
                      disabled={processName === 'CODE_FORM_SUBMISSION'}
                      autoFocus={isCodeSent}
                    />
                  }
                  indicator={
                    processName === 'CODE_FORM_SUBMISSION' && (
                      <TextComponent color="accent">
                        <SpinnerComponent />
                      </TextComponent>
                    )
                  }
                />
              </MarginComponent>
            </Fade>

            <Fade
              duration={ANIMATION_DEFAULT_DURATION}
              delay={!isCodeSent ? ANIMATION_DEFAULT_DURATION : 0}
              when={!isCodeSent}
              mountOnEnter
              unmountOnExit
              collapse
              bottom
            >
              <MarginComponent top={48}>
                <PressControlComponent
                  disabled={processName === 'PHONE_FORM_SUBMISSION'}
                  theme="primary"
                  type="submit"
                  fill={isMobile}
                  hasSpinner={processName === 'PHONE_FORM_SUBMISSION'}
                >
                  {ownDictionary.sendCodeActionText}
                </PressControlComponent>
              </MarginComponent>
            </Fade>
          </>
        }
        backAction={null}
      />
    </FormControlComponent>
  );
};
